import {
  ChangeDetectionStrategy,
  Component,
  HostListener,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { EditableFieldComponent } from '../../../../../shared/ui/editable-field/editable-field.component';
import {
  AbstractControl,
  FormGroup,
  FormsModule,
  NonNullableFormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatIcon } from '@angular/material/icon';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatTabsModule } from '@angular/material/tabs';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { RolesManagementStore } from '../../store/roles-management.store';
import { ColorPickerComponent } from '../color-picker/color-picker.component';
import { PermissionsComponent } from '../permissions/permissions.component';
import { CloseButtonComponent } from '../../../../../shared/ui/close-button/close-button.component';
import { RoleUsersComponent } from '../role-users/role-users.component';
import { RoleNameValidators } from '../../../../../shared/validators';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { IAddRoleForm, IColorOption } from '../../interfaces';
import { draftDialogData } from '../../../../../shared/constants';
import { SharedUIService, DialogHelpersService } from '../../../../../shared/services';
import { ADMIN } from '../../../../core/constants';
import { ColorOptions } from '../../constants';
import { TranslationService } from '../../../../shared/services/translation.service';
import { TranslatePipe } from "../../../../shared/pipes/translationPipe";

@Component({
  selector: 'app-add-role-dialog',
  imports: [
    EditableFieldComponent,
    MatIcon,
    MatDialogModule,
    MatTabsModule,
    MatButtonModule,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ColorPickerComponent,
    PermissionsComponent,
    CloseButtonComponent,
    RoleUsersComponent,
    ScrollingModule,
    TranslatePipe
],
  templateUrl: './add-role-dialog.component.html',
  styleUrl: './add-role-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddRoleDialogComponent implements OnInit {
  protected readonly store = inject(RolesManagementStore);
  protected readonly translation = inject(TranslationService);
  protected readonly dialogRef = inject(MatDialogRef<AddRoleDialogComponent>);
  protected readonly roleNameValidator = inject(RoleNameValidators);
  protected readonly sharedUI = inject(SharedUIService);


  readonly #fb = inject(NonNullableFormBuilder);
  readonly #roleNameValidator = inject(RoleNameValidators);
  readonly #dialogHelpers = inject(DialogHelpersService);

  private readonly dialogData = inject<{
    selectedTabIndex: number | undefined;
  }>(MAT_DIALOG_DATA);

  protected readonly draftDialogData = draftDialogData;
  protected readonly ADMIN = ADMIN;
  protected readonly showColorPicker = signal(false);

  protected selectedTabIndex = 0;

  protected readonly form = this.#fb.group<IAddRoleForm>({
    name: this.#fb.control<string>(''),
    description: this.#fb.control<string>(''),
    icon: this.#fb.group({
      name: this.#fb.control<string>('devices'),
      color: this.#fb.control<string>(ColorOptions[3].color),
      background: this.#fb.control<string>(ColorOptions[3].background),
    }),
  });

  protected showConfirmationDialog = () => {
    this.sharedUI.showConfirmationWithObservable(
      this.#dialogHelpers.getDraftDialogData(),
      this.closeDialog
    );
  };

  get iconGroup() {
    return this.form.controls.icon;
  }

  get iconColor() {
    return this.iconGroup.controls.color;
  }

  get iconBackground() {
    return this.iconGroup.controls.background;
  }

  ngOnInit(): void {
    this.patchFormFromTempRole();
    this.setValidators();
    this.setupDialogCloseHandler();
    this.selectedTabIndex = this.dialogData?.selectedTabIndex ?? 0;
  }

  protected closeDialog = () => {
    this.sharedUI.closeDialog(this.dialogRef);
  };

  protected hasChanges(): boolean {
    return (
      (this.form.dirty && this.form.valid) || this.store.permissionsChanged()
    );
  }

  protected toggleColorPicker(): void {
    this.showColorPicker.set(!this.showColorPicker());
  }

  protected onColorSelected(color: IColorOption): void {
    this.iconGroup.patchValue({
      color: color.color,
      background: color.background,
    });

    this.markGroupTouchedAndDirty(this.iconGroup);
    this.markGroupTouchedAndDirty(this.form);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const colorPickerElement = document.querySelector(
      '.add-role-dialog__color-picker',
    );
    const toggleElement = document.querySelector(
      '.add-role-dialog__color-toggle',
    );

    if (
      this.showColorPicker() &&
      !colorPickerElement?.contains(event.target as Node) &&
      !toggleElement?.contains(event.target as Node)
    ) {
      this.showColorPicker.set(false);
    }
  }


  private patchFormFromTempRole(): void {
    const temp = this.store.tempRole();
    const iconDefaults = ColorOptions[3];

    this.form.patchValue({
      name: temp?.name ?? '',
      description: temp?.description ?? '',
      icon: {
        name: temp?.icon?.name ?? 'devices',
        color: temp?.icon?.color ?? iconDefaults.color,
        background: temp?.icon?.background ?? iconDefaults.background,
      },
    });

    if (temp && temp.name === ADMIN) {
      this.form.controls.name.disable({ emitEvent: false });
      this.translation
        .getTranslation('adminDescription')
        .subscribe((translatedDesc) => {
          this.form.controls.description.setValue(translatedDesc);
        });
    }
  }

  private setValidators(): void {
    const isCreating = !this.store.tempRole()?.name;
    const nameControl = this.form.controls.name;

    const validators = isCreating ? [Validators.required] : [];
    validators.push(
      this.roleNameValidator.roleNameValidator(this.store.entities()),
    );
    nameControl.setValidators(validators);
    nameControl.updateValueAndValidity();
  }

  private setupDialogCloseHandler() {
    this.sharedUI.confirmOnCloseWithObservable(
      this.dialogRef,
      () => this.hasChanges(),
      this.#dialogHelpers.getDraftDialogData(),
    );
  }
  private markGroupTouchedAndDirty(group: AbstractControl): void {
    group.markAsTouched();
    group.markAsDirty();

    if ((group as FormGroup).controls) {
      Object.values((group as FormGroup).controls).forEach((control) => {
        control.markAsTouched();
        control.markAsDirty();
      });
    }
  }
}
